import { FormattedNumber } from 'react-intl'
import { withHandlers } from 'recompose'

import { getDateTimeFormat } from 'i18n/utils'
import { currencyFromSymbol } from 'shared/currency-utilities'
import { isMobileAgent } from 'shared/responsive-helpers'
import TableCell from 'shared/components/TableCell'
import ConfirmationModal from './ConfirmationModal'
import { reservationSelector, statusSelector, cancellationSelector } from '../selectors'
import { reservationCancelAmendPolicyPendingSelector, cancelReservationPendingSelector } from 'grs/selectors'
import { STATUS_ACTIVE } from 'grs/constants'
import {
    downloadReceipt,
    getReservationCancelAmendPolicy,
    closeCancellationModal,
    cancelReservation,
    confirmCancelReservation,
} from '../actions'
import { startReservationExtension } from 'extending/actions'

import Button from '@components/Button'
import { RESERVATION_IN_PROGRESS } from '@booking/constants'

const enhance = compose(
    connect(
        createStructuredSelector({
            cancelAmendPolicyReservationPending: reservationCancelAmendPolicyPendingSelector,
            cancelReservationPending: cancelReservationPendingSelector,
            reservation: reservationSelector,
            status: statusSelector,
            cancellation: cancellationSelector,
        }),
        {
            downloadReceipt,
            getReservationCancelAmendPolicy,
            startReservationExtension,
            cancelReservation,
            closeCancellationModal,
            confirmCancelReservation,
        }
    ),
    withHandlers({
        onClick: ({ reservationId, onClick }) => () => onClick(reservationId),
        onClickDownloadReceipt: ({ reservationId, downloadReceipt }) => e => {
            e.stopPropagation()
            downloadReceipt(reservationId, isMobileAgent() ? open() : undefined)
        },
    })
)

const Reservation = ({
    reservation: {
        confirmationNumber,
        createdDate,
        isMonthlyPass,
        location,
        fromDate,
        toDate,
        currencySymbol,
        amount,
        reservationId,
        allowExtend,
        canCancel,
        status: reservationStatus,
    },
    status,
    cancellation,
    onClick,
    onClickDownloadReceipt,
    getReservationCancelAmendPolicy,
    startReservationExtension,
    closeCancellationModal,
    confirmCancelReservation,
    cancelAmendPolicyReservationPending,
    cancelReservationPending,
    cancelReservation,
}) => {
    const { waitingForCancelConfirmation, reservationId: activeCancellationId } = cancellation

    const onCancelReservation = e => {
        e.stopPropagation()
        getReservationCancelAmendPolicy(reservationId)
        cancelReservation(reservationId)
    }

    return (
        <>
            <div className="table-row" onClick={onClick}>
                <TableCell fieldName={<T>dashboard.label.transaction</T>} className="transaction">
                    <span>{confirmationNumber}</span>
                    <span>{createdDate.format(getDateTimeFormat())}</span>
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.type</T>} className="type">
                    <T>{isMonthlyPass ? 'dashboard.label.subscription' : 'dashboard.label.booking'}</T>
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.location</T>} className="location">
                    {location}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.range.s.date</T>} className="start">
                    {fromDate.format(getDateTimeFormat())}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.range.e.date</T>} className="end">
                    {toDate.format(getDateTimeFormat())}
                    {allowExtend && (
                        <Button
                            btnStyle="link"
                            iconLeft="arrow-right"
                            href="/extending"
                            onClick={e => {
                                e.stopPropagation()
                                startReservationExtension(reservationId)
                                getReservationCancelAmendPolicy(reservationId)
                            }}>
                            <T>dashboard.label.extend</T>
                        </Button>
                    )}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.price</T>} className="price">
                    <FormattedNumber value={amount} style="currency" currency={currencyFromSymbol(currencySymbol)} />
                    {canCancel && status === STATUS_ACTIVE && (
                        <Button
                            btnStyle="outline"
                            size="small"
                            className="ml-auto m-md-0"
                            onClick={onCancelReservation}>
                            <T>dashboard.label.cancel.reservation</T>
                        </Button>
                    )}
                </TableCell>
                <TableCell className="actions">
                    {reservationStatus !== RESERVATION_IN_PROGRESS && (
                        <>
                            <button className="download" onClick={onClickDownloadReceipt} />
                            <div className="action-hover">
                                <T>dashboard.label.receipt</T>
                            </div>
                        </>
                    )}
                </TableCell>
            </div>
            {waitingForCancelConfirmation && activeCancellationId === reservationId && (
                <ConfirmationModal
                    reservationId={reservationId}
                    cancellationData={cancellation}
                    confirm={confirmCancelReservation}
                    cancelReservationPending={cancelReservationPending}
                    cancelAmendPolicyReservationPending={cancelAmendPolicyReservationPending}
                    closeModal={closeCancellationModal}
                />
            )}
        </>
    )
}

export default enhance(Reservation)
